import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { isIndia } from "@/lib/edition";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { Newspaper, ArrowUpRight, ArrowsClockwise, EnvelopeSimple } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function NewsDigestSubscribe() {
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState(isIndia ? "India" : "");
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => {}); }, []);
  const subscribe = async () => {
    if (!email.trim() || !country) return toast.error("Enter your email and pick a country");
    setBusy(true);
    try {
      const { data } = await api.post("/news/subscribe", { email: email.trim(), country });
      toast.success(data.already ? "You're already subscribed — welcome back!" : `Subscribed! Weekly ${country} digest is on its way.`);
      setEmail("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 rounded-2xl border border-secondary/30 bg-secondary/5 p-6" data-testid="news-digest-subscribe">
      <div className="flex items-center gap-2"><EnvelopeSimple size={18} weight="duotone" className="text-secondary" /><h2 className="font-heading text-lg font-medium">Get a weekly digest</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">{isIndia ? "Top education & alumni headlines from across India, delivered every week." : "Top education & alumni headlines from your country, delivered every week."}</p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" type="email" className="rounded-full sm:flex-1" data-testid="digest-email" />
        {!isIndia && (
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="rounded-full sm:w-52" data-testid="digest-country"><SelectValue placeholder="Choose country" /></SelectTrigger>
          <SelectContent>{countries.map((c) => <SelectItem key={c.country} value={c.country}>{c.country}</SelectItem>)}</SelectContent>
        </Select>
        )}
        <Button onClick={subscribe} disabled={busy} className="rounded-full gap-1.5" data-testid="digest-subscribe"><EnvelopeSimple size={15} /> {busy ? "Subscribing…" : "Subscribe"}</Button>
      </div>
    </div>
  );
}

export default function News() {
  useSeo("News", "Education & alumni news from around the world, aggregated and tagged.");
  const [data, setData] = useState(null);
  const [tag, setTag] = useState("");

  useEffect(() => {
    setData(null);
    api.get("/news", { params: tag ? { tag } : {} }).then((r) => setData(r.data)).catch(() => setData({ items: [], tags: [] }));
  }, [tag]);

  return (
    <div className="mx-auto max-w-5xl px-5 py-14">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary"><Newspaper size={24} weight="duotone" /></span>
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Education & alumni news</h1>
          <p className="text-sm text-muted-foreground">{isIndia ? "Education & alumni news from across India, linked back to the original source." : "Gathered from trusted sources worldwide, tagged and linked back to the original."}</p>
        </div>
      </div>

      <NewsDigestSubscribe />

      {data?.tags?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="news-tags">
          <button onClick={() => setTag("")} className={`rounded-full border px-3 py-1 text-sm ${!tag ? "border-secondary bg-secondary text-secondary-foreground" : "border-border text-muted-foreground hover:border-secondary/50"}`}>All</button>
          {data.tags.map((t) => (
            <button key={t} onClick={() => setTag(t)} className={`rounded-full border px-3 py-1 text-sm ${tag === t ? "border-secondary bg-secondary text-secondary-foreground" : "border-border text-muted-foreground hover:border-secondary/50"}`}>{t}</button>
          ))}
        </div>
      )}

      {data === null ? <p className="mt-10 flex items-center gap-2 text-muted-foreground"><ArrowsClockwise size={16} className="animate-spin" /> Gathering the latest news…</p>
        : data.items.length === 0 ? <p className="mt-10 text-muted-foreground">No news yet — check back soon.</p>
        : (
          <div className="mt-8 divide-y divide-border/60">
            {data.items.map((n) => (
              <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="group flex items-start gap-4 py-4" data-testid={`news-item-${n.id}`}>
                <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl border border-border/60 bg-muted">
                  {n.image_url ? <img src={n.image_url} alt={n.source} className="h-full w-full object-contain p-1.5" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Newspaper size={20} className="text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-secondary">{n.source}</span>
                    {n.country && <span className="text-xs text-muted-foreground">· {n.country}</span>}
                    {(n.tags || []).slice(0, 1).map((t) => <Badge key={t} variant="outline" className="rounded-full text-[10px] font-normal">{t}</Badge>)}
                  </div>
                  <h3 className="font-heading text-lg font-medium leading-snug transition-colors group-hover:text-secondary">{n.title}</h3>
                  {n.summary && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{n.summary}</p>}
                </div>
                <ArrowUpRight size={18} className="mt-1 shrink-0 text-muted-foreground transition-colors group-hover:text-secondary" />
              </a>
            ))}
          </div>
        )}
    </div>
  );
}
